
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Lock } from "lucide-react";
import { cleanupAuthState, logAuthEvent, forcePageRefresh, isDesktop } from "@/utils/authUtils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import PasswordRecovery from "@/components/auth/PasswordRecovery";
import PasswordReset from "@/components/auth/PasswordReset";

type AuthMode = 'signin' | 'signup' | 'recovery' | 'reset';

export default function Auth() {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for different URL parameters
  const confirmed = searchParams.get('confirmed');
  const planId = searchParams.get('plan');
  const isRecovery = searchParams.get('recovery') === 'true';
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');
  
  useEffect(() => {
    if (confirmed === 'true') {
      setMode('signin');
      toast.success("Email confirmed successfully! You can now sign in.");
      // Clear the URL parameter but keep plan if present
      if (planId) {
        window.history.replaceState({}, document.title, `/auth?plan=${planId}`);
      } else {
        window.history.replaceState({}, document.title, '/auth');
      }
    }
    
    // Check if user is arriving via password recovery link
    if (type === 'recovery' && accessToken && refreshToken) {
      setMode('reset');
    } else if (isRecovery) {
      setMode('recovery');
    }
  }, [confirmed, planId, isRecovery, accessToken, refreshToken, type]);
  
  // If user is already logged in, redirect to appropriate page
  // BUT: if this is a password recovery session, force them to reset password first
  useEffect(() => {
    if (user) {
      // Check if this is a password recovery session that needs a reset
      const isPasswordRecoverySession = type === 'recovery' && (accessToken || refreshToken);
      
      if (isPasswordRecoverySession && mode !== 'reset') {
        // Force password reset mode for recovery sessions
        setMode('reset');
        return;
      }
      
      // Only redirect if not in reset mode and not a recovery session
      if (mode !== 'reset' && !isPasswordRecoverySession) {
        if (planId) {
          // Redirect to pricing with subscribe parameter if plan was selected
          navigate(`/pricing?subscribe=${planId}`);
        } else {
          // Otherwise redirect to home
          navigate("/");
        }
      }
    }
  }, [user, navigate, planId, mode, type, accessToken, refreshToken]);
  
  const clearForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setTermsAccepted(false);
    setError("");
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (mode === 'signup' && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (mode === 'signup' && !termsAccepted) {
      setError("You must agree to the Terms & Conditions to create an account");
      return;
    }
    
    setIsLoading(true);
    logAuthEvent(`Starting ${mode} process`);
    
    try {
      // Clean up any existing auth state before attempting new auth
      cleanupAuthState();
      
      if (mode === 'signup') {
        const { error, success, needsConfirmation } = await signUp(email, password);
        if (error) {
          logAuthEvent("Signup failed", error);
          setError(error.message);
        } else if (success) {
          logAuthEvent("Signup successful");
          
          // Update profile with terms acceptance
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase
              .from('profiles')
              .update({ 
                terms_accepted: true, 
                terms_accepted_at: new Date().toISOString() 
              })
              .eq('id', user.id);
          }
          
          // Clear form and switch to sign-in mode
          clearForm();
          setMode('signin');
          
          if (needsConfirmation) {
            toast.success("Please check your email and click the confirmation link, then sign in here.");
          } else {
            toast.success("Account created successfully! You can now sign in.");
          }
        }
      } else if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) {
          logAuthEvent("Signin failed", error);
          setError(error.message);
        } else {
          logAuthEvent("Signin successful, redirecting");
          
          // Redirect based on whether a plan was selected
          if (planId) {
            // Force page refresh for desktop to ensure clean state
            if (isDesktop()) {
              logAuthEvent("Desktop with plan redirect, forcing page refresh");
              setTimeout(() => {
                forcePageRefresh(`/pricing?subscribe=${planId}`);
              }, 500);
            } else {
              navigate(`/pricing?subscribe=${planId}`);
            }
          } else {
            // Original redirect logic to home page
            if (isDesktop()) {
              logAuthEvent("Desktop detected, forcing page refresh");
              setTimeout(() => {
                forcePageRefresh('/');
              }, 500);
            } else {
              navigate("/");
            }
          }
        }
      }
    } catch (err: any) {
      logAuthEvent("Auth error", err);
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleModeChange = (newMode: AuthMode) => {
    setMode(newMode);
    clearForm();
  };

  const handlePasswordResetComplete = () => {
    setMode('signin');
    toast.success("Password updated successfully! You can now sign in with your new password.");
  };
  
  // If user is already logged in, component will redirect via useEffect
  if (user && mode !== 'reset') return null;
  
  const renderContent = () => {
    if (mode === 'recovery') {
      return <PasswordRecovery onBackToSignIn={() => handleModeChange('signin')} />;
    }
    
    if (mode === 'reset') {
      return <PasswordReset onComplete={handlePasswordResetComplete} />;
    }
    
    // Regular sign in/sign up form
    return (
      <>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold font-display tracking-tight">
            {mode === 'signup' ? "Create CraftyJoy Account" : "Welcome to CraftyJoy"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mode === 'signup'
              ? "Sign up to get creative ideas for your children"
              : "Sign in to your CraftyJoy account"}
          </p>
        </div>
        
        {error && (
          <div className="mb-6 p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex gap-1 items-center">
              <Mail className="h-4 w-4" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="flex gap-1 items-center">
              <Lock className="h-4 w-4" /> Password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11"
            />
          </div>
          
          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="flex gap-1 items-center">
                <Lock className="h-4 w-4" /> Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>
          )}
          
          {mode === 'signup' && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                required
              />
              <div className="text-sm leading-5">
                <label htmlFor="terms" className="text-foreground cursor-pointer">
                  I agree to the{" "}
                  <Link 
                    to="/terms" 
                    className="text-primary underline underline-offset-4 hover:text-primary/80"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </Link>
                </label>
              </div>
            </div>
          )}
          
          <Button
            type="submit"
            className="w-full h-11 text-base"
            disabled={isLoading || (mode === 'signup' && !termsAccepted)}
          >
            {isLoading ? "Processing..." : mode === 'signup' ? "Sign Up" : "Sign In"}
          </Button>
        </form>
        
        {mode === 'signin' && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => handleModeChange('recovery')}
              className="text-primary underline underline-offset-4 text-sm"
            >
              Forgot your password?
            </button>
          </div>
        )}
        
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => handleModeChange(mode === 'signup' ? 'signin' : 'signup')}
            className="text-primary underline underline-offset-4 text-sm"
          >
            {mode === 'signup'
              ? "Already have an account? Sign In"
              : "Don't have an account? Sign Up"}
          </button>
        </div>
      </>
    );
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <div className="relative pt-24 pb-16 flex-1 flex flex-col items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-[500px] w-[1000px] -translate-x-1/2 bg-gradient-to-br from-primary/20 via-blue-50 to-background blur-3xl"></div>
        </div>
        
        <div className="relative w-full max-w-md">
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-10">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
